import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BADGE, fmtPrice, API, imgUrl } from "@/lib/site";
import { Gamepad2, Loader2, Lock, ShieldCheck } from "lucide-react";

export const CheckoutDialog = ({ pack, open, onOpenChange }) => {
  const [epicName, setEpicName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEpicName("");
      setEmail("");
      setError("");
      setLoading(false);
    }
  }, [open, pack]);

  if (!pack) return null;
  const b = BADGE[pack.badge] || BADGE.premium;

  const submit = async (e) => {
    e.preventDefault();
    if (epicName.trim().length < 3) {
      setError("Bitte gib deinen Epic Games Username ein (mind. 3 Zeichen).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        pack_id: pack.id,
        epic_name: epicName.trim(),
        email: email.trim() || null,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError("Zahlung konnte nicht gestartet werden.");
        setLoading(false);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d[0]?.msg : d;
      setError(typeof msg === "string" ? msg : "Etwas ist schiefgelaufen. Bitte versuche es erneut.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog" className="max-w-lg border-white/10 bg-[#0f0f11] text-white p-0 overflow-hidden gap-0">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex gap-4 p-6 border-b border-white/10">
            <img src={imgUrl(pack.image)} alt={pack.name} className="w-20 h-24 object-cover object-top rounded-sm border border-white/10" />
            <div className="flex-1">
              <div className="inline-block font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full border mb-2" style={{ color: b.color, borderColor: b.color }}>
                {b.label}
              </div>
              <DialogTitle className="font-display font-bold text-xl leading-tight">{pack.name}</DialogTitle>
              <DialogDescription className="sr-only">Kaufe {pack.name} und gib deinen Epic Games Username für die Lieferung an.</DialogDescription>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-display font-bold text-lg">{fmtPrice(pack.price)}</span>
                {pack.original_price && (
                  <span className="font-mono text-xs text-[#8b8b93] line-through">{fmtPrice(pack.original_price)}</span>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="p-6 space-y-5">
            <div>
              <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#e2ff00] mb-2">
                <Gamepad2 size={14} /> Epic Games Username · erforderlich
              </label>
              <input
                data-testid="checkout-epic-input"
                autoFocus
                value={epicName}
                onChange={(e) => setEpicName(e.target.value)}
                placeholder="z.B. NinjaOG_2017"
                className="w-full bg-black/40 border border-[#e2ff00]/40 focus:border-[#e2ff00] rounded-md px-4 py-3.5 text-base outline-none transition-colors placeholder:text-[#54545c] focus:ring-2 focus:ring-[#e2ff00]/20"
              />
              <p className="mt-2 text-xs text-[#8b8b93]">Wir liefern den Account direkt an diesen Epic-Namen. Bitte Schreibweise prüfen.</p>
            </div>

            <div>
              <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-[#8b8b93] mb-2">
                E-Mail (optional · für die Bestätigung)
              </label>
              <input
                data-testid="checkout-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="du@email.de"
                className="w-full bg-black/40 border border-white/10 focus:border-white/30 rounded-md px-4 py-3.5 text-base outline-none transition-colors placeholder:text-[#54545c]"
              />
            </div>

            {error && <div data-testid="checkout-error" className="text-sm text-[#ff0055] font-mono">{error}</div>}

            <button
              data-testid="checkout-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#e2ff00] text-black font-mono text-sm uppercase tracking-[0.2em] py-4 rounded-full hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {loading ? "Weiterleitung…" : `Sicher bezahlen · ${fmtPrice(pack.price)}`}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-[#8b8b93] font-mono">
              <ShieldCheck size={12} /> Karte, Klarna &amp; mehr · sicher über Stripe
            </p>
            <p className="text-center text-[11px] text-[#8b8b93]">
              🎁 Rabattcode? Gib ihn im nächsten Schritt bei Stripe ein.
            </p>
          </form>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
